import type { ITrendSeries } from 'components/Intelligence/TrendChart';
import { NO_VALUE, formatMetricReading } from 'components/Intelligence/formatMetric';
import { metricIdentityFor } from 'containers/Intelligence/registry/metricIdentifiers';
import { tokens } from 'containers/Intelligence/theme';
import { MetricFormat } from 'containers/Intelligence/types';

import { orbitSeriesStop, orbitTokens } from './styles';
import { OrbitMetricResult, OrbitSeriesPoint, OrbitVisualization } from './types';

/**
 * The panel's eight series stops, in order.
 *
 * The first four are the donut legend's designed colours (figma-spec-v2.md §7b M6, `4952:8550` /
 * `8556` / `8562` / `8568`); the fourth is the frame's muted grey, which inks a decorative dot and a
 * bar fill and never text, so the panel's lifted `textMuted` is not the right value for it. No frame
 * draws a fifth series, so the tail is client-assigned from the Intelligence hues (AF-10).
 *
 * PERX3-REDESIGN round 5: each of those eight is now the FALLBACK of its ramp stop, so the flag-OFF
 * chart draws exactly these eight and the flag-ON chart draws one ordered tonal ramp instead of
 * eight unrelated hues.
 */
const DESIGNED_FOURTH_SERIES = orbitTokens.seriesFourth;

export const ORBIT_SERIES_PALETTE: string[] = [
  orbitTokens.accent,
  orbitTokens.action,
  orbitTokens.danger,
  DESIGNED_FOURTH_SERIES,
  tokens.color.warning,
  tokens.color['accent-slate'],
  tokens.color.positive,
  tokens.color.negative
].map((hue, index) => orbitSeriesStop(index + 1, hue));

/** A plotted point that the wire may have sent no reading for (PLA-9563 §6c). */
export interface OrbitPlottedPoint {
  label: string;
  /** `null` where the metric returned no reading: never substituted with a zero. */
  value: number | null;
  /** The reading in the metric's own unit, or `—`. */
  display: string;
  color: string;
}

export interface OrbitLineChartModel {
  kind: 'line';
  /** The plotted metric's own name, which the image export slugs into the file name. */
  title: string;
  series: ITrendSeries[];
  pointLabels: string[];
  points: OrbitSeriesPoint[];
  format: MetricFormat;
  currency?: string;
}

export interface OrbitDonutChartModel {
  kind: 'donut';
  title: string;
  segments: OrbitPlottedPoint[];
  centerValue: string;
  points: OrbitSeriesPoint[];
  format: MetricFormat;
  currency?: string;
}

export interface OrbitBarChartModel {
  kind: 'bar';
  title: string;
  columns: OrbitPlottedPoint[];
  points: OrbitSeriesPoint[];
  format: MetricFormat;
  currency?: string;
}

export type OrbitChartModel = OrbitLineChartModel | OrbitDonutChartModel | OrbitBarChartModel;

/**
 * The metric's business NAME, so Orbit never prints a raw `O-COM-011` in a user-facing explanation
 * (PLA-9563 §3a).
 *
 * The PAYLOAD's own `metric_results[].metric_name` wins, because it names the metric the server
 * actually computed this answer from. The registry label is the fallback, and it is what an evidence
 * chip resolves through: `answer.evidence` carries bare ids, and a metric cited there need not be
 * one the turn returned a result for. The raw id is the last resort and never the first.
 */
export const orbitMetricName = (metricId: string, payloadName?: string): string => {
  if (payloadName !== undefined && payloadName !== '') {
    return payloadName;
  }

  return metricIdentityFor(metricId)?.label ?? metricId;
};

export const orbitMetricFormat = (metricId: string): MetricFormat =>
  metricIdentityFor(metricId)?.format ?? 'count';

const COHORT_FORMAT: Partial<Record<string, MetricFormat>> = {
  distribution_share: 'percent',
  population_count: 'count'
};

/** A cohort row states its own unit: a band share is a percentage whatever the metric's format. */
export const orbitResultFormat = (
  metricId: string,
  cohort?: { aggregation_class: string } | null
): MetricFormat => COHORT_FORMAT[cohort?.aggregation_class ?? ''] ?? orbitMetricFormat(metricId);

/**
 * Every Orbit reading goes through the Intelligence cards' own formatter, so the same metric prints
 * identically on both surfaces. A reading the wire did not send is the placeholder, never a zero.
 */
export const formatMetricValue = (
  value: number | null | undefined,
  format: MetricFormat = 'count',
  currency?: string
): string =>
  value === null || value === undefined || !Number.isFinite(value)
    ? NO_VALUE
    : formatMetricReading(value, format, currency);

export const humaniseSeriesKey = (key: string): string => {
  const spaced = key.replace(/_/g, ' ').trim();

  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

/** A cohort cell's own name when the card sent one (category keys are opaque hashes), else the key. */
export const seriesPointLabel = (result: OrbitMetricResult, key: string): string => {
  const displayName = result.cohort?.cells.find(cell => cell.dimension_key === key)?.display_name;

  return humaniseSeriesKey(displayName ?? key);
};

export const seriesColor = (index: number): string =>
  ORBIT_SERIES_PALETTE[index % ORBIT_SERIES_PALETTE.length];

/**
 * The growth-driver bars run a different order from the donut legend: `4952:8104` / `8112` / `8120`
 * ink the three fills #268AFF, #6D5DFC and #64709A, where `4952:8550` onwards starts the legend on
 * #6D5DFC. The frame draws only three rows, so a fourth falls back to the series palette.
 */
export const ORBIT_DRIVER_PALETTE: string[] = [
  orbitTokens.action,
  orbitTokens.accent,
  DESIGNED_FOURTH_SERIES
].map((hue, index) => orbitSeriesStop(index + 1, hue));

export const driverColor = (index: number): string =>
  index < ORBIT_DRIVER_PALETTE.length ? ORBIT_DRIVER_PALETTE[index] : seriesColor(index);

/** A reading the wire actually sent. `null` and a non-finite number are both "not measured". */
export const isMeasured = (value: number | null | undefined): value is number =>
  value !== null && value !== undefined && Number.isFinite(value);

const isFinitePoint = (point: OrbitSeriesPoint): boolean => isMeasured(point.value);

/** `series` is optional on the wire (contract §6); every traversal goes through this. */
export const seriesOf = (result: OrbitMetricResult): OrbitSeriesPoint[] => result.series ?? [];

const withSeries = (results: OrbitMetricResult[]): OrbitMetricResult[] =>
  results.filter(result => seriesOf(result).length > 0);

/**
 * Union of every metric's series keys, in first-seen order. Metabase omits an empty bucket, so two
 * metrics of the same question can cover different windows; the x axis is their union and each
 * metric is then read BY KEY onto it, never by array position.
 */
const unionSeriesKeys = (results: OrbitMetricResult[]): string[] => {
  const keys: string[] = [];
  const seen = new Set<string>();

  results.forEach(result =>
    seriesOf(result).forEach(point => {
      if (!seen.has(point.key)) {
        seen.add(point.key);
        keys.push(point.key);
      }
    })
  );

  return keys;
};

const toTrendModel = (
  results: OrbitMetricResult[],
  currency?: string
): OrbitLineChartModel | null => {
  /*
    TrendChart draws nothing from a single reading, so a one-point series is dropped here rather
    than leaving an empty chart slot in the answer. A series that carries a GAP is kept: PLA-9563
    §6b, where a governed time series with one unmeasured period used to suppress the whole chart
    because every point had to be finite.
  */
  const trends = withSeries(results).filter(
    result => seriesOf(result).length > 1 && seriesOf(result).some(isFinitePoint)
  );

  if (trends.length === 0) {
    return null;
  }

  // Keys are period labels ('2026-06'), so sorting them puts the axis in chronological order for the
  // ISO-ish keys Metabase emits and in a stable, shared order for anything else.
  const axisKeys = unionSeriesKeys(trends).sort();

  return {
    kind: 'line',
    // A line answer plots every metric it was given; the first is the one the answer leads with.
    title: orbitMetricName(trends[0].metric_id, trends[0].metric_name),
    series: trends.map((result, index) => {
      const valueByKey = new Map(seriesOf(result).map(point => [point.key, point.value]));

      return {
        name: orbitMetricName(result.metric_id, result.metric_name),
        color: seriesColor(index),
        // A key this metric does not cover, and a period it covered with no reading, both become
        // NaN, which TrendChart breaks the line on rather than plotting as a zero
        // (`ITrendSeries.points`, `connectNulls={false}`).
        points: axisKeys.map(key => {
          const value = valueByKey.get(key);

          return isMeasured(value) ? value : Number.NaN;
        })
      };
    }),
    pointLabels: axisKeys.map(humaniseSeriesKey),
    points: trends.flatMap(seriesOf),
    format: orbitMetricFormat(trends[0].metric_id),
    currency
  };
};

// A donut or a column chart draws ONE distribution, so the first metric that carries a series is
// the one plotted; the table above the chart still lists every metric.
const firstDistribution = (results: OrbitMetricResult[]): OrbitMetricResult | null => {
  const distribution = withSeries(results).find(result =>
    seriesOf(result).some(point => isMeasured(point.value) && point.value > 0)
  );

  return distribution === undefined ? null : distribution;
};

const plottedPoints = (
  distribution: OrbitMetricResult,
  format: MetricFormat,
  color: (index: number) => string,
  currency?: string
): OrbitPlottedPoint[] =>
  seriesOf(distribution).map((point, index) => ({
    label: seriesPointLabel(distribution, point.key),
    value: isMeasured(point.value) ? point.value : null,
    display: formatMetricValue(point.value, format, currency),
    color: color(index)
  }));

const toDonutModel = (
  results: OrbitMetricResult[],
  currency?: string
): OrbitDonutChartModel | null => {
  const distribution = firstDistribution(results);

  if (distribution === null) {
    return null;
  }

  const points = seriesOf(distribution);
  const format = orbitResultFormat(distribution.metric_id, distribution.cohort);
  const total = points.reduce(
    (sum, point) => sum + (isMeasured(point.value) && point.value > 0 ? point.value : 0),
    0
  );

  return {
    kind: 'donut',
    title: orbitMetricName(distribution.metric_id, distribution.metric_name),
    segments: plottedPoints(distribution, format, seriesColor, currency),
    centerValue: formatMetricValue(total, format, currency),
    points,
    format,
    currency
  };
};

const toBarModel = (results: OrbitMetricResult[], currency?: string): OrbitBarChartModel | null => {
  const distribution = firstDistribution(results);

  if (distribution === null) {
    return null;
  }

  const points = seriesOf(distribution);
  const format = orbitResultFormat(distribution.metric_id, distribution.cohort);

  return {
    kind: 'bar',
    title: orbitMetricName(distribution.metric_id, distribution.metric_name),
    columns: plottedPoints(distribution, format, driverColor, currency),
    points,
    format,
    currency
  };
};

export const resolveOrbitChart = (
  visualization: OrbitVisualization,
  results: OrbitMetricResult[],
  currency?: string
): OrbitChartModel | null => {
  switch (visualization) {
    case 'line':
      return toTrendModel(results, currency);
    case 'donut':
      return toDonutModel(results, currency);
    case 'bar':
      return toBarModel(results, currency);
    default:
      return null;
  }
};

/**
 * TOOLTIP formatter only — never an axis `tickFormatter`. The shipped charts hand their tooltip
 * formatter only the reading, so `n_customers` is recovered by value; a generated axis tick that
 * happens to equal a reading would otherwise be decorated with a cohort size it does not describe
 * (pass the model's own value formatter as `formatAxisValue` for that). A reading shared by two
 * points with different counts is ambiguous and shows no count rather than a wrong one.
 */
export const buildValueFormatter = (
  points: OrbitSeriesPoint[],
  formatCount: (nCustomers: number) => string,
  format: MetricFormat = 'count',
  currency?: string
): ((value: number) => string) => {
  const countsByValue = new Map<number, number | null>();

  points.forEach(point => {
    if (!isMeasured(point.value)) {
      return;
    }

    const known = countsByValue.get(point.value);

    if (known === undefined) {
      countsByValue.set(point.value, point.n_customers);
    } else if (known !== point.n_customers) {
      countsByValue.set(point.value, null);
    }
  });

  return value => {
    const count = countsByValue.get(value);
    const formatted = formatMetricValue(value, format, currency);

    return count === undefined || count === null
      ? formatted
      : `${formatted} · ${formatCount(count)}`;
  };
};
